/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/display-name */
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import AppLayout from "layout/AppLayout";
import SelectInput from "components/common/SelectInput";
import axios from "axiosClient";
import ListingTable2 from "components/ListingTable2";
import { formatGmt5, GMT5_LABEL } from "utils/timezone";
import {
  StatusBadge,
  TypeBadge,
  discountLabel,
  scopeLabel,
  usageLabel,
} from "./shared";

const columns = [
  {
    Header: "Code",
    accessor: "code",
    cellClass: "w-15",
    Cell: (props) => (
      <NavLink to={`/app/promocodes/${props.row.original._id}`}>
        <strong>{props.value}</strong>
      </NavLink>
    ),
    sortType: "basic",
  },
  {
    Header: "Works at",
    accessor: "type",
    cellClass: "text-muted w-10",
    Cell: (props) => <TypeBadge type={props.value} />,
    sortType: "basic",
  },
  {
    Header: "Discount",
    accessor: "discountValue",
    cellClass: "text-muted w-10",
    Cell: (props) => <>{discountLabel(props.row.original)}</>,
    sortType: "basic",
  },
  {
    Header: "Applies to",
    accessor: "exams",
    cellClass: "text-muted w-20",
    Cell: (props) => <>{scopeLabel(props.row.original)}</>,
    disableSortBy: true,
  },
  {
    Header: `Valid from (${GMT5_LABEL})`,
    accessor: "startsAt",
    cellClass: "text-muted w-15",
    Cell: (props) => <>{formatGmt5(props.value)}</>,
    sortType: "basic",
  },
  {
    Header: `Valid until (${GMT5_LABEL})`,
    accessor: "endsAt",
    cellClass: "text-muted w-15",
    Cell: (props) => <>{formatGmt5(props.value)}</>,
    sortType: "basic",
  },
  {
    Header: "Used",
    accessor: "usedCount",
    cellClass: "text-muted w-10",
    Cell: (props) => <>{usageLabel(props.row.original)}</>,
    sortType: "basic",
  },
  {
    Header: "Status",
    accessor: "status",
    cellClass: "text-muted w-10",
    Cell: (props) => <StatusBadge status={props.value} />,
    sortType: "basic",
  },
  {
    Header: "Created by",
    accessor: "createdBy",
    cellClass: "text-muted w-10",
    Cell: (props) => <>{props.value?.username || "—"}</>,
    disableSortBy: true,
  },
];

const emptyFilters = {
  search: "",
  status: "",
  type: "",
  exam: "",
  discountType: "",
  includeArchived: false,
};

const PromocodesList = () => {
  const navigate = useNavigate();
  const cols = useMemo(() => columns, []);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState(emptyFilters);
  const [examOptions, setExamOptions] = useState([]);
  const [showData, setShowData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [selectedPageSize, setSelectedPageSize] = useState(30);

  useEffect(() => {
    axios
      .get("/api/promocode-options")
      .then((result) => setExamOptions(result.data.exams || []))
      .catch(() => setExamOptions([]));
  }, []);

  const handleSearchSubmit = (page = 1, perPage = 30, values = filters) => {
    setLoading(true);
    axios
      .post("/api/promocodes", { page, per_page: perPage, ...values })
      .then((result) => {
        setItems(result.data.items);
        setTotal(result.data.total);
        setCurrentPage(result.data.page);
        setTotalPage(result.data.pageCount);
        setShowData(true);
      })
      .catch(() => toast.error("Could not load promocodes"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    handleSearchSubmit(1, selectedPageSize, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters({ ...filters, [name]: type === "checkbox" ? checked : value });
  };

  return (
    <AppLayout>
      <div className="dashboard-wrapper">
        <Card className="h-100 m-4">
          <CardBody>
            <div className="d-flex justify-content-between align-items-start">
              <CardTitle>Promocodes</CardTitle>
              <Button color="primary" onClick={() => navigate("add")}>
                Add new
              </Button>
            </div>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                handleSearchSubmit(1, selectedPageSize, filters);
              }}
            >
              <Row className="text-left">
                <Col md="3">
                  <FormGroup>
                    <Label>Code or description</Label>
                    <Input
                      type="text"
                      name="search"
                      value={filters.search}
                      onChange={handleChange}
                    />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label>Status</Label>
                    <SelectInput
                      name="status"
                      placeholder="All"
                      isClearable
                      value={filters.status}
                      onChange={(status) => setFilters({ ...filters, status })}
                      options={[
                        { label: "Active", value: "active" },
                        { label: "Scheduled", value: "scheduled" },
                        { label: "Expired", value: "expired" },
                        { label: "Fully used", value: "exhausted" },
                        { label: "Disabled", value: "disabled" },
                        { label: "Archived", value: "archived" },
                      ]}
                    />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label>Exam</Label>
                    <SelectInput
                      name="exam"
                      placeholder="All"
                      isClearable
                      value={filters.exam}
                      onChange={(exam) => setFilters({ ...filters, exam })}
                      options={examOptions.map((option) => option.exam)}
                    />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label>Works at</Label>
                    <SelectInput
                      name="type"
                      placeholder="All"
                      isClearable
                      value={filters.type}
                      onChange={(type) => setFilters({ ...filters, type })}
                      options={[
                        { label: "Public checkout", value: "public" },
                        { label: "POS till", value: "pos" },
                      ]}
                    />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label>Discount type</Label>
                    <SelectInput
                      name="discountType"
                      placeholder="All"
                      isClearable
                      value={filters.discountType}
                      onChange={(discountType) =>
                        setFilters({ ...filters, discountType })
                      }
                      options={[
                        { label: "Percentage", value: "percentage" },
                        { label: "Fixed amount", value: "amount" },
                      ]}
                    />
                  </FormGroup>
                </Col>
                <Col md="12" className="d-flex justify-content-between">
                  <FormGroup check className="align-self-center">
                    <Label check>
                      <Input
                        type="checkbox"
                        name="includeArchived"
                        checked={filters.includeArchived}
                        onChange={handleChange}
                      />{" "}
                      Include archived
                    </Label>
                  </FormGroup>
                  <div>
                    <Button
                      color="light"
                      className="mr-2"
                      onClick={() => {
                        setFilters(emptyFilters);
                        handleSearchSubmit(1, selectedPageSize, emptyFilters);
                      }}
                    >
                      Reset
                    </Button>
                    <Button
                      color="primary"
                      onClick={() =>
                        handleSearchSubmit(1, selectedPageSize, filters)
                      }
                    >
                      Search
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>

        {showData && (
          <ListingTable2
            columns={cols}
            data={items}
            totalItemCount={total}
            handleSearch={handleSearchSubmit}
            loading={loading}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPage={totalPage}
            selectedPageSize={selectedPageSize}
            setSelectedPageSize={setSelectedPageSize}
            searchValues={filters}
            withNumeration={false}
          />
        )}
      </div>
    </AppLayout>
  );
};

export default PromocodesList;
